/**
 * Token Index Service
 *
 * Handles fetching token index data from Supabase Edge Functions.
 * Token indexes represent different crypto index funds (CAP50, TREASURE, APEX, etc.)
 */

import { supabase } from './supabaseService';
import { MOCK_ETH_PORTFOLIO_ENABLED, FOLIO_DEV_OVERRIDE_ENABLED, FOLIO_DEV_OVERRIDE, MOCK_PDF_REPORT_ENABLED } from '@/constants/featureFlags';

/**
 * Token Index interface matching database schema
 *
 * Key fields:
 * - id: Immutable database primary key. Use for all internal logic.
 * - slug: URL-safe identifier for routing (e.g., 'all-market', 'big-three').
 * - vault_address: On-chain vault contract address (e.g., '0xacD05F3Ed...').
 *   This is the single source of truth for portfolio identity.
 * - on_chain_symbol: Actual ERC20 token symbol on-chain (e.g., 'QINDEX', 'QAPEX')
 *   Use this for displaying the symbol to users.
 * - display_name: User-facing name shown in UI (e.g., 'All Market')
 *
 * @deprecated symbol - Use id for logic, on_chain_symbol for display
 */
export interface TokenIndex {
  id: number;
  slug: string;
  name: string;
  display_name: string;
  icon_url: string;
  on_chain_symbol: string;
  description?: string;
  vault_address?: string;
  /**
   * EVM chain ID where the vault is deployed. Defaults to 8453 (Base) for legacy
   * rows. Per ADR-0007, portfolio identity is `(chain_id, vault_address)`.
   */
  chain_id?: number;
  /**
   * Minimum investment in USD for this portfolio. Optional — when absent, the
   * UI falls back to a chain-wide default (e.g., $1 on Base, higher on mainnet).
   */
  min_investment_usd?: number;
  /**
   * QIndexFolioZapper contract — USDC mint entrypoint (the approval spender for
   * mint, and the target of `zapMint`). Per the Reserve Folio backend. Optional
   * for legacy rows that predate the multichain Folio migration.
   */
  zapper_address?: string;
  /**
   * QIndexFolioAdapter contract — redeem target (the approval spender for the
   * Folio share token, and the target of `redeem`). Per the Reserve Folio backend.
   */
  adapter_address?: string;
  pdf_report_url?: string | null;
  enabled: boolean;
  display_order: number;
  created_at?: string;
  updated_at?: string;
  /** @deprecated Use id for logic, on_chain_symbol for display */
  symbol?: string;
}

/**
 * Response from get-token-indexes Edge Function
 */
interface TokenIndexResponse {
  success: boolean;
  data?: TokenIndex[];
  metadata?: {
    total: number;
    enabled: number;
  };
  error?: string;
  message?: string;
}

/**
 * Fetch all token indexes from Supabase
 *
 * @param enabledOnly - If true, only returns enabled token indexes
 * @returns Array of token indexes
 */
export async function fetchTokenIndexes(enabledOnly: boolean = false): Promise<TokenIndex[]> {
  try {
    // Call Supabase Edge Function
    const { data, error } = await supabase.functions.invoke<TokenIndexResponse>(
      'get-token-indexes',
      {
        body: { enabled_only: enabledOnly }
      }
    );

    if (error) {
      throw new Error(`Failed to fetch token indexes: ${error.message}`);
    }

    if (!data || !data.success) {
      throw new Error(data?.error || 'Invalid response from server');
    }

    const rows = data.data || [];

    // Dev-only: inject a fake mainnet portfolio so QA can validate the multi-chain
    // UX (badge, min, switch-chain prompt, gas warning) without backend having
    // deployed a real mainnet vault. Gated by MOCK_ETH_PORTFOLIO_ENABLED which
    // only fires when import.meta.env.DEV is true.
    if (MOCK_ETH_PORTFOLIO_ENABLED) {
      rows.push({
        id: 999,
        slug: 'mock-stocks',
        name: 'Mock Stocks',
        display_name: 'Mock Stocks (dev)',
        icon_url: 'https://nlbkxjvyeighuhkrrbtk.supabase.co/storage/v1/object/public/token-index-icons/apex.png',
        on_chain_symbol: 'MSTOCK',
        description: 'DEV ONLY — fake mainnet portfolio for testing the multi-chain UX.',
        vault_address: '0xDEAD000000000000000000000000000000000000',
        chain_id: 1,
        min_investment_usd: 100,
        enabled: true,
        display_order: 99,
      });
    }

    // Dev-only: point the matching portfolio (by on_chain_symbol) at the Reserve
    // Folio contracts, WITHOUT mutating the shared token_indexes row that prod reads.
    // Applied at the data source so every consumer (ChoosePortfolio, usePortfolioFlow,
    // useVaultComposition, …) sees the folio addresses consistently. Gated by
    // import.meta.env.DEV inside the flag. See .docs/runbooks/cutover-folio.md.
    if (FOLIO_DEV_OVERRIDE_ENABLED) {
      const target = rows.find(r => r.on_chain_symbol === FOLIO_DEV_OVERRIDE.symbol);
      if (target) {
        target.vault_address = FOLIO_DEV_OVERRIDE.folioAddress;
        target.zapper_address = FOLIO_DEV_OVERRIDE.zapperAddress;
        target.adapter_address = FOLIO_DEV_OVERRIDE.adapterAddress;
        target.chain_id = FOLIO_DEV_OVERRIDE.chainId;
      }
    }

    if (MOCK_PDF_REPORT_ENABLED) {
      rows.forEach(row => {
        row.pdf_report_url = `${window.location.origin}/mock-report.pdf`;
      });
    }

    return rows;
  } catch (error) {
    throw error;
  }
}

/**
 * Fetch only enabled token indexes
 * Convenience wrapper for fetchTokenIndexes(true)
 *
 * @returns Array of enabled token indexes
 */
export async function fetchEnabledTokenIndexes(): Promise<TokenIndex[]> {
  return fetchTokenIndexes(true);
}

/**
 * Get a specific token index by id
 *
 * @param id - Token index id (e.g., 1, 2, 3)
 * @returns Token index or undefined if not found
 */
export async function getTokenIndexById(id: number): Promise<TokenIndex | undefined> {
  const indexes = await fetchTokenIndexes();
  return indexes.find(index => index.id === id);
}

/**
 * Get a specific token index by slug
 *
 * @param slug - Token index slug (e.g., 'all-market', 'big-three')
 * @returns Token index or undefined if not found
 */
export async function getTokenIndexBySlug(slug: string): Promise<TokenIndex | undefined> {
  const indexes = await fetchTokenIndexes();
  return indexes.find(index => index.slug === slug);
}

/**
 * Check if a token index exists and is enabled
 *
 * @param id - Token index id
 * @returns True if index exists and is enabled
 */
export async function isTokenIndexEnabled(id: number): Promise<boolean> {
  const index = await getTokenIndexById(id);
  return index?.enabled ?? false;
}

/**
 * @deprecated Use getTokenIndexById instead
 */
export async function getTokenIndexBySymbol(symbol: string): Promise<TokenIndex | undefined> {
  const indexes = await fetchTokenIndexes();
  return indexes.find(index => index.symbol === symbol);
}
